import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { ToastContainer } from './Toast.jsx';

/**
 * Notification context for managing global notifications
 */
const NotificationContext = createContext(null);

/**
 * Notification reducer for managing notification state
 */
const notificationReducer = function(state, action) {
  switch (action.type) {
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [...state.notifications, action.payload]
      };

    case 'REMOVE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter(function(notification) {
          return notification.id !== action.payload;
        })
      };

    case 'CLEAR_ALL_NOTIFICATIONS':
      return {
        ...state,
        notifications: []
      };

    case 'UPDATE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.map(function(notification) {
          return notification.id === action.payload.id
            ? { ...notification, ...action.payload.updates }
            : notification;
        })
      };

    default:
      return state;
  }
};

/**
 * NotificationProvider component for providing notification context
 *
 * @param {Object} props - NotificationProvider properties
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.position - Default toast position
 * @param {number} props.maxNotifications - Maximum number of notifications
 * @returns {JSX.Element} NotificationProvider component
 */
const NotificationProvider = function NotificationProvider({
  children,
  position = 'top-right',
  maxNotifications = 5
}) {

  const initialState = {
    notifications: [],
    position: position,
    maxNotifications: maxNotifications
  };

  const [state, dispatch] = useReducer(notificationReducer, initialState);

  const removeNotification = useCallback(function(id) {
    dispatch({
      type: 'REMOVE_NOTIFICATION',
      payload: id
    });
  }, []);

  const addNotification = useCallback(function(notification) {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      variant: 'info',
      duration: 5000,
      dismissible: true,
      showIcon: true,
      ...notification,
      onDismiss: function() {
        removeNotification(id);
        if (notification.onDismiss) {
          notification.onDismiss();
        }
      }
    };

    dispatch({
      type: 'ADD_NOTIFICATION',
      payload: newNotification
    });

    return id;
  }, [removeNotification]);

  const clearAllNotifications = useCallback(function() {
    dispatch({
      type: 'CLEAR_ALL_NOTIFICATIONS'
    });
  }, []);

  const updateNotification = useCallback(function(id, updates) {
    dispatch({
      type: 'UPDATE_NOTIFICATION',
      payload: { id, updates }
    });
  }, []);

  const showSuccess = useCallback(function(message, options = {}) {
    return addNotification({
      variant: 'success',
      title: options.title || 'Успех!',
      message,
      ...options
    });
  }, [addNotification]);

  const showError = useCallback(function(message, options = {}) {
    return addNotification({
      variant: 'error',
      title: options.title || 'Грешка!',
      message,
      duration: 0, // Don't auto-dismiss errors
      ...options
    });
  }, [addNotification]);

  const showWarning = useCallback(function(message, options = {}) {
    return addNotification({
      variant: 'warning',
      title: options.title || 'Внимание!',
      message,
      ...options
    });
  }, [addNotification]);

  const showInfo = useCallback(function(message, options = {}) {
    return addNotification({
      variant: 'info',
      title: options.title || 'Информация',
      message,
      ...options
    });
  }, [addNotification]);

  const contextValue = {
    notifications: state.notifications,
    position: state.position,
    maxNotifications: state.maxNotifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    updateNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <ToastContainer
        toasts={state.notifications.slice(0, state.maxNotifications)}
        position={state.position}
        maxToasts={state.maxNotifications}
      />
    </NotificationContext.Provider>
  );
};

/**
 * useNotifications hook for accessing notification context
 *
 * @returns {Object} Notification context methods and state
 */
const useNotifications = function() {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }

  return context;
};

/**
 * withNotifications HOC for providing notification methods to components
 *
 * @param {React.Component} Component - Component to wrap
 * @returns {React.Component} Wrapped component with notification props
 */
const withNotifications = function(Component) {
  return function WithNotificationsComponent(props) {
    const notifications = useNotifications();

    return React.createElement(Component, {
      ...props,
      notifications
    });
  };
};

/**
 * NotificationBoundary component for handling notification errors
 *
 * @param {Object} props - NotificationBoundary properties
 * @param {React.ReactNode} props.children - Child components
 * @param {Function} props.onError - Error handler
 * @returns {JSX.Element} NotificationBoundary component
 */
class NotificationBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Notification error:', error, errorInfo);

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return null; // Fail silently for notifications
    }

    return this.props.children;
  }
}

/**
 * Global notification utilities for use outside React components
 */
let globalNotificationContext = null;

const setGlobalNotificationContext = function(context) {
  globalNotificationContext = context;
};

const globalNotifications = {
  success: function(message, options) {
    if (globalNotificationContext) {
      return globalNotificationContext.showSuccess(message, options);
    }
    console.warn('Global notifications not initialized');
    return null;
  },

  error: function(message, options) {
    if (globalNotificationContext) {
      return globalNotificationContext.showError(message, options);
    }
    console.error('Notification Error:', message);
    return null;
  },

  warning: function(message, options) {
    if (globalNotificationContext) {
      return globalNotificationContext.showWarning(message, options);
    }
    console.warn('Notification Warning:', message);
    return null;
  },

  info: function(message, options) {
    if (globalNotificationContext) {
      return globalNotificationContext.showInfo(message, options);
    }
    console.info('Notification Info:', message);
    return null;
  },

  remove: function(id) {
    if (globalNotificationContext) {
      return globalNotificationContext.removeNotification(id);
    }
    return null;
  },

  clear: function() {
    if (globalNotificationContext) {
      return globalNotificationContext.clearAllNotifications();
    }
    return null;
  }
};

/**
 * NotificationManager component that sets up global context
 *
 * @param {Object} props - NotificationManager properties
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.position - Toast position
 * @param {number} props.maxNotifications - Maximum notifications
 * @returns {JSX.Element} NotificationManager component
 */
const NotificationManager = function NotificationManager({
  children,
  position = 'top-right',
  maxNotifications = 5
}) {
  return (
    <NotificationBoundary>
      <NotificationProvider
        position={position}
        maxNotifications={maxNotifications}
      >
        <GlobalNotificationSetup />
        {children}
      </NotificationProvider>
    </NotificationBoundary>
  );
};

/**
 * Component to set up global notification context
 */
const GlobalNotificationSetup = function GlobalNotificationSetup() {
  const notifications = useNotifications();

  React.useEffect(function() {
    setGlobalNotificationContext(notifications);

    return function() {
      setGlobalNotificationContext(null);
    };
  }, [notifications]);

  return null;
};

NotificationProvider.displayName = 'NotificationProvider';
NotificationManager.displayName = 'NotificationManager';
NotificationBoundary.displayName = 'NotificationBoundary';

export default NotificationManager;
export {
  NotificationProvider,
  NotificationContext,
  useNotifications,
  withNotifications,
  NotificationBoundary,
  globalNotifications
};
